export const SOCIAL_NETWORKS = [
	"facebook",
	"instagram",
	"instagram-standalone",
	"threads",
	"x",
	"linkedin",
	"linkedin-page",
	"tiktok",
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export const SOCIAL_DRIVERS = ["fake", "postiz", "key"] as const;
export type SocialDriver = (typeof SOCIAL_DRIVERS)[number];

export const SOCIAL_POST_STATUSES = ["draft", "scheduled", "publishing", "published", "failed"] as const;
export type SocialPostStatus = (typeof SOCIAL_POST_STATUSES)[number];

export const SOCIAL_POST_MODES = ["now", "schedule", "draft"] as const;
export type SocialPostMode = (typeof SOCIAL_POST_MODES)[number];

export interface SocialImage {
	id: string;
	path: string;
}

export interface SocialChannel {
	id: string;
	organisationId: string;
	network: SocialNetwork;
	handle: string;
	providerId: string;
	picture: string | null;
	connectedAt: Date;
	disconnectedAt: Date | null;
}

export interface SocialPost {
	id: string;
	organisationId: string;
	providerId: string | null;
	integrationId: string | null;
	body: string;
	networks: SocialNetwork[];
	scheduledAt: Date | null;
	publishedAt: Date | null;
	status: SocialPostStatus;
	releaseUrl: string | null;
	createdAt: Date;
}

export interface SocialMetric {
	label: string;
	percentageChange: number;
	latest: string;
}

export interface SocialPostWindow {
	start: Date;
	end: Date;
}

/**
 * Postiz is a separate process. Key never imports its source.
 * The browser never holds Postiz credentials or network tokens.
 * One API key is one Postiz organisation. Key claims each new integration
 * onto the workspace that connected it, so another workspace cannot see it.
 */
export interface SocialPlatform {
	listChannels(organisationKey: string): Promise<SocialChannel[]>;
	listPosts(organisationKey: string, window: SocialPostWindow): Promise<SocialPost[]>;
	createPost(organisationKey: string, input: CreateSocialPostInput): Promise<SocialPost>;
	connectUrl(organisationKey: string, network: SocialNetwork): Promise<string>;
	deletePost(organisationKey: string, postId: string): Promise<void>;
	disconnectChannel(organisationKey: string, channelId: string): Promise<void>;
	upload(organisationKey: string, file: Blob, name: string): Promise<SocialImage>;
	analytics(organisationKey: string, integrationId: string, days: number): Promise<SocialMetric[]>;
}

export interface CreateSocialPostInput {
	body: string;
	networks: SocialNetwork[];
	channelIds: string[];
	scheduledAt: Date | null;
	mode: SocialPostMode;
	images: SocialImage[];
}

export function isSocialNetwork(value: string): value is SocialNetwork {
	return (SOCIAL_NETWORKS as readonly string[]).includes(value);
}
